import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconGripVertical } from '@tabler/icons-react';
import type { PartOrderLine } from '@techgitter/contracts';
import type { WorkItem } from '../workitems/workItemTypes';
import { JobRow } from './JobRow';
import { AppointmentRow } from './AppointmentRow';
import { CheckInRow } from './CheckInRow';
import { isAppointmentItem, isCheckInItem, toJobRowProps, toAppointmentRowProps, toCheckInRowProps } from './workItemToRow';
import { normalizeStatus } from '../workitems/utils/workItemUtils';
import { getStatusOptionsForType } from '../workitems/utils/workItemStatusConfig';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import type { DragData } from './TechnicianCardList';

interface SortableRowProps {
  item: WorkItem;
  /** Owning technician (assignee key). */
  assignee: string;
  index: number;
  onEditItem?: (item: WorkItem) => void;
  /** Convert a standalone appointment into a job (gated to appointment rows). */
  onConvertToJob?: (item: WorkItem) => void;
  /**
   * Delete a job. When set, the job row shows a "···" overflow menu with a
   * "Delete job" action that calls this; the dashboard opens the
   * JobDeleteDialog, which is the confirmation. Gated to job rows and to
   * Admin / Manager, matching the API.
   */
  onDeleteItem?: (item: WorkItem) => Promise<void>;
  /** Renders the expansion panel shown below the row when its chevron is toggled. */
  renderExpansion?: (item: WorkItem) => React.ReactNode;
  /** Part-order lines for the row's parts-ready badge. */
  partLines?: PartOrderLine[];
  /** Change a job's status from the row's status pill. */
  onStatusChange?: (item: WorkItem, dvValue: string) => void;
  /** Right-click the drag handle to open the reassign menu at the cursor. */
  onReassignRequest?: (item: WorkItem, position: { top: number; left: number }) => void;
}

/**
 * A drag-sortable wrapper around JobRow / AppointmentRow. The grip handle is the
 * activator (listeners go there, not the whole row); the card carries the node
 * ref + transform so dnd-kit animates it in place.
 */
export const SortableRow: React.FC<SortableRowProps> = ({ item, assignee, index, onEditItem, onConvertToJob, onDeleteItem, renderExpansion, partLines, onStatusChange, onReassignRequest }) => {
  const [expanded, setExpanded] = useState(false);
  const currentUser = useCurrentUser();
  const data: DragData = {
    type: isAppointmentItem(item) ? 'appointment' : 'job',
    jobId: item.guid,
    technicianId: assignee,
    index,
  };

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.guid, data });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : undefined,
  };

  const dragHandleProps = { ...attributes, ...listeners };
  const toggleExpand = () => setExpanded((e) => !e);

  // Convert-to-job is offered only for standalone appointments (type
  // 'appointment', not 'job-appointment' which already has a job) that aren't
  // cancelled — mirroring the legacy WorkItemAccordionRow gate.
  const convertToJob =
    onConvertToJob &&
    item.type === 'appointment' &&
    normalizeStatus(item.status || item.appointmentStatus || '') !== 'cancelled'
      ? () => onConvertToJob(item)
      : undefined;

  const handleHandleContextMenu = onReassignRequest
    ? (e: React.MouseEvent) => {
        e.preventDefault();
        onReassignRequest(item, { top: e.clientY, left: e.clientX });
      }
    : undefined;

  // Delete offered only for job rows (appointments/check-ins have their own
  // affordances), and only to the roles the API accepts a job delete from.
  // The confirmation is the dashboard's JobDeleteDialog.
  const canDelete =
    !!onDeleteItem && item.type === 'job' && !!currentUser?.hasAnyRole('Admin', 'Manager');

  // Parts chip counts (non-cancelled part lines + how many received).
  const nonCancelledParts = (partLines ?? []).filter((l) => l.status !== 'cancelled');
  const partsCount = nonCancelledParts.length;
  const partsReceived = nonCancelledParts.filter((l) => l.status === 'received').length;

  // When expanded the handle is rendered externally (left of the wrapper), so
  // the row itself omits its built-in handle to avoid the wrapper swallowing it.
  const row = isCheckInItem(item) ? (
    <CheckInRow
      {...toCheckInRowProps(item)}
      onExpand={renderExpansion ? toggleExpand : undefined}
      expanded={expanded}
      hideHandle={expanded && !!renderExpansion}
      setNodeRef={setNodeRef}
      setActivatorNodeRef={setActivatorNodeRef}
      dragHandleProps={dragHandleProps}
      onHandleContextMenu={handleHandleContextMenu}
      style={style}
    />
  ) : isAppointmentItem(item) ? (
    <AppointmentRow
      {...toAppointmentRowProps(item)}
      onEdit={onEditItem ? () => onEditItem(item) : undefined}
      onConvertToJob={convertToJob}
      onExpand={renderExpansion ? toggleExpand : undefined}
      expanded={expanded}
      hideHandle={expanded && !!renderExpansion}
      setNodeRef={setNodeRef}
      setActivatorNodeRef={setActivatorNodeRef}
      dragHandleProps={dragHandleProps}
      onHandleContextMenu={handleHandleContextMenu}
      style={style}
      onStatusChange={onStatusChange ? (dvValue) => onStatusChange(item, dvValue) : undefined}
      statusOptions={onStatusChange ? getStatusOptionsForType(item.type) : undefined}
    />
  ) : (
    <JobRow
      {...toJobRowProps(item)}
      partsCount={partsCount}
      partsReceived={partsReceived}
      onEdit={onEditItem ? () => onEditItem(item) : undefined}
      onDelete={canDelete ? () => { void onDeleteItem?.(item); } : undefined}
      onExpand={renderExpansion ? toggleExpand : undefined}
      expanded={expanded}
      hideHandle={expanded && !!renderExpansion}
      setNodeRef={setNodeRef}
      setActivatorNodeRef={setActivatorNodeRef}
      dragHandleProps={dragHandleProps}
      onHandleContextMenu={handleHandleContextMenu}
      style={style}
      onStatusChange={onStatusChange ? (dvValue) => onStatusChange(item, dvValue) : undefined}
      statusOptions={onStatusChange ? getStatusOptionsForType(item.type) : undefined}
    />
  );

  // Collapsed: the standard row alone. Expanded: the grab handle stays outside
  // (left of) a grey-surfaced wrapper whose header is the row, with the
  // customer + log cards below it.
  if (expanded && renderExpansion) {
    return (
      <div className="work-item-expanded-outer">
        <div className="job-row__handle work-item-expanded__handle" ref={setActivatorNodeRef} {...dragHandleProps} onContextMenu={handleHandleContextMenu}>
          <IconGripVertical size={18} stroke={1.5} />
        </div>
        <div className={`work-item-expanded ${isCheckInItem(item) ? 'work-item-expanded--checkin' : isAppointmentItem(item) ? 'work-item-expanded--appointment' : 'work-item-expanded--job'}`}>
          <div className="work-item-expanded__header">{row}</div>
          {renderExpansion(item)}
        </div>
      </div>
    );
  }

  return row;
};
